<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('automaticSentences.categories') }}
      </v-toolbar-title>
    </template>
    <template #content>
      <v-skeleton-loader
        v-if="loading"
        class="m-auto"
        type="table"
        transition="scale-transition"
      />
      <VTableRemote
        v-else
        ref="tableRemoteRef"
        :iconHeader="hasPermission('automaticSentences.categories.add') ? 'add' : null"
        :items="categories"
        isSearchable
        hideDefaultFooter
        :isInfinitScroll="true"
        :pagination="pagination"
        :headers="tableHeaders"
        :isCustomIcon="true"
        :itemActions="actions"
        :title="$t('automaticSentences.categories')"
        :centerColumns="['action']"
        :notSortableColumns="['action']"
        :sortableColumns="['name.en', 'name.fr']"
        @iconAction="onClickAction"
        @sendQueryParams="fetchQuery"
      >
        <template #withInfiniteScroll>
          <div v-intersect="endIntersect" />
        </template>
      </VTableRemote>
    </template>
  </BoxWithTitle>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import CategoryForm from '@/components/forms/CategoryForm';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import perpage from '@/constants/pagination';

export default {
    components: {
        BoxWithTitle,
        VTableRemote,
    },
    data() {
        return {
            categories: [],
            languesServiceProvider: [],
            loading: false,
            pagination: {},
            queryParams: {
                perpage,
                page: 1,
                pagination: true,
            },
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'teamLangs',
        ]),
        tableHeaders() {
            return this.languesServiceProvider.map(header => ({
                text: this.$t(`forms.labels.languageName.${header}`),
                value: `name.${header}`,
                sortable: true,
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('automaticSentences.categories.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('automaticSentences.categories.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        this.loading = true;
        this.languesServiceProvider = this.teamLangs;
        await this.getCategories();
        this.loading = false;
    },
    methods: {
        async getCategories() {
            const { data } = await this.$rq.getSentenceCategories(this.queryParams);
            this.pagination = data.pagination || {};
            this.categories = [...this.categories, ...data.categories];
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('automaticSentences.addCategory'),
                component: Vue.component('CategoryForm', CategoryForm),
                showLangs: true,
                isForm: true,
                onConfirm: requestPayload => this.addCategory(requestPayload),
            });
        },
        onClickAction(category, action) {
            if (!action && category === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                this.$root.$emit('showModal', {
                    title: this.$t('automaticSentences.updateCategory'),
                    component: Vue.component('CategoryForm', CategoryForm),
                    showLangs: true,
                    isForm: true,
                    payload: { category: cloneDeep(category), isEdition: true },
                    onConfirm: requestPayload => this.updateCategory(category.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('automaticSentences.removeCategory'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('automaticSentences.confirmRemoveCategory') },
                    onConfirm: () => this.removeCategory(category.id),
                    isForm: false,
                });
            }
        },
        async addCategory(requestPayload) {
            await this.$rq.addSentenceCategory(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.categoryAdded'),
            });
            await this.fetchQuery();
        },
        async updateCategory(id, requestPayload) {
            if (!requestPayload) return;
            await this.$rq.updateSentenceCategory(id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.categoryModified'),
            });
            await this.fetchQuery();
        },
        async removeCategory(id) {
            await this.$rq.removeSentenceCategory(id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.categoryRemoved'),
            });
            await this.fetchQuery();
        },
        fetchQuery({ order, ...params } = {}) {
            this.loading = true;
            this.queryParams = {
                ...this.queryParams,
                ...params,
                page: 1,
                order: order || '',
            };
            this.pagination = {};
            this.categories = [];
            this.getCategories();
            this.loading = false;
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getCategories();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
